import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  IconButton
} from '@mui/material';
import EditorConvertToHTML  from '../../utils/TextEditor';
import { CloudUpload, Delete } from '@mui/icons-material';
import { createPolicy } from '../../services/policyAPIs'; 

const PolicyForm = ({ onSuccess, pTitle }) => {

  const [bodyHTML, setBodyHTML] = useState('');

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);
  const [type, setType] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each image must be less than 5MB');
        return false;
      }
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        setError('Only JPEG, PNG, or GIF images are allowed');
        return false;
      }
      return true;
    });

    setImages(prev => [...prev, ...validFiles]);
    if (validFiles.length === files.length) {
      setError('');
    }
  };

  const handleDeleteImage = (indexToDelete) => {
    setImages(images.filter((_, index) => index !== indexToDelete));
    setError('');
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !type) {
      setError('Title and Type are required');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('body', bodyHTML);
    formData.append('type', type);
    
    images.forEach((image) => {
      formData.append('images', image);
    });

    try {
      await createPolicy(formData);
      setTitle('');
      setBody('');
      setType('');
      setError('');
      setImages([]);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Failed to save policy. Try again.');
    }
  };

  return (
    <Card sx={{
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" color="primary" gutterBottom>
          {pTitle}
        </Typography>
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2
          }}
        >  
          <TextField
            fullWidth
            label="Policy Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
          <TextField
            select
            fullWidth
            label="Policy Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            required
          >
            <MenuItem value="OFFICE_POLICY">Office Policy</MenuItem>
            <MenuItem value="HR_HANDBOOK">HR Handbook</MenuItem>
          </TextField>

          {/* body */}
          <Typography variant="body1" color="textSecondary">Policy Body</Typography>
          <EditorConvertToHTML onChange={setBodyHTML} />

          {/* Image Upload Section */}
          <Box
            sx={{
              border: '2px dashed #90caf9',
              borderRadius: '8px',
              padding: 2,
              backgroundColor: '#f0f5ff',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2196f3',
                backgroundColor: '#e3f2fd'
              }
            }}
          >
            <Typography variant="body1" color="#1565c0" gutterBottom>
              Upload Images (Optional)
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleButtonClick}
              sx={{
                backgroundColor: '#2196f3',
                '&:hover': {
                  backgroundColor: '#1e88e5'
                },
                mb: 2
              }}
            >
              Choose Images
            </Button>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            {images.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="#3d52a0">
                  Selected Images:
                </Typography>
                {images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 0.5
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {image.name}
                    </Typography>
                    <IconButton
                      onClick={() => handleDeleteImage(index)}
                      size="small"
                      sx={{ color: '#f44336' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          
          {error && (
            <Typography color="error" sx={{ flexShrink: 0 }}>{error}</Typography>
          )}
        </Box>
      </CardContent>
      
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #ccc',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor: 'var(--card-bg, white)'
        }}
      >
        <Button
          type="submit"
          variant="contained"
          sx={{
            backgroundColor: '#2196f3',
            '&:hover': {
              backgroundColor: '#1e88e5'
            }
          }}
          onClick={handleSubmit}
        >
          Save Policy
        </Button>
      </Box>
    </Card>
  );
};

export default PolicyForm;